/* The section loads this file with defer, so by the time it runs
   document.currentScript is null and the data-hero-root-id it carried is out
   of reach. Instead of one root id, find every hero root on the page — the
   theme editor can render more than one — and let each part mark its own node
   ready, so a re-run (editor re-render) never wires the same node twice. */
for (const root of document.querySelectorAll('[id^="sbov3hero-"]')) {
  initHeroCarousel(root);
  initHeroConcerns(root);
  initHeroPreorder(root);
  initHeroPromo(root);
}

function initHeroCarousel(root) {
  const carousel = root.querySelector('[data-hero-carousel]');
  if (!carousel || carousel.dataset.ready) return;
  carousel.dataset.ready = 'true';

  const slides = Array.from(carousel.querySelectorAll('[data-carousel-slide]'));
  const dots = Array.from(carousel.querySelectorAll('[data-carousel-dot]'));
  const previousButton = carousel.querySelector('[data-carousel-prev]');
  const nextButton = carousel.querySelector('[data-carousel-next]');
  const status = carousel.querySelector('[data-carousel-status]');
  let activeIndex = 0;
  let pointerStartX = null;
  let suppressClick = false;

  if (slides.length < 2) return;

  function showSlide(nextIndex, announce = true) {
    activeIndex = (nextIndex + slides.length) % slides.length;
    slides.forEach((slide, index) => {
      const active = index === activeIndex;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.toggleAttribute('inert', !active);
    });
    dots.forEach((dot, index) => {
      const active = index === activeIndex;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (status) {
      status.setAttribute('aria-live', announce ? 'polite' : 'off');
      status.textContent = `Image ${activeIndex + 1} of ${slides.length}`;
    }
  }

  function chooseSlide(index) {
    showSlide(index, true);
  }

  previousButton && previousButton.addEventListener('click', () => chooseSlide(activeIndex - 1));
  nextButton && nextButton.addEventListener('click', () => chooseSlide(activeIndex + 1));
  dots.forEach(dot => dot.addEventListener('click', () => chooseSlide(Number(dot.dataset.carouselDot))));

  carousel.addEventListener('keydown', event => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    chooseSlide(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
  });

  carousel.addEventListener('pointerdown', event => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    pointerStartX = event.clientX;
  });
  carousel.addEventListener('pointerup', event => {
    if (pointerStartX === null) return;
    const distance = event.clientX - pointerStartX;
    pointerStartX = null;
    if (Math.abs(distance) < 44) return;
    suppressClick = true;
    chooseSlide(activeIndex + (distance < 0 ? 1 : -1));
    window.setTimeout(() => { suppressClick = false; }, 0);
  });
  carousel.addEventListener('pointercancel', () => { pointerStartX = null; });
  carousel.addEventListener('click', event => {
    if (!suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
    suppressClick = false;
  }, true);

  document.addEventListener('shopify:block:select', event => {
    const selectedSlide = event.target && event.target.closest('[data-carousel-slide]');
    if (!selectedSlide || !carousel.contains(selectedSlide)) return;
    showSlide(Number(selectedSlide.dataset.index), false);
  });
}

/* Starter concerns: open the routine builder on the chosen concern. The
   builder loads lazily (assets/sbo-v3-routine-loader.js), so it is loaded
   first, then its tab is pressed and the page moves to it. Preventing the
   default here tells the loader's own link handler to stand aside. */
function initHeroConcerns(root) {
  if (root.dataset.starterReady) return;
  root.dataset.starterReady = 'true';
  root.addEventListener('click', event => {
    const chip = event.target.closest('[data-hero-concern]');
    if (!chip || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    const slug = chip.dataset.heroConcern;
    const open = () => {
      const tab = Array.from(document.querySelectorAll('.sbov3rb [role="tab"]')).find(t => t.dataset.target === slug);
      if (tab && tab.getAttribute('aria-selected') !== 'true') tab.click();
      const anchor = document.getElementById('routine-builder');
      if (!anchor) return;
      if (location.hash === '#routine-builder') anchor.scrollIntoView();
      else location.hash = 'routine-builder';
    };
    const loader = window.SBORoutineLoader;
    if (loader && typeof loader.load === 'function') loader.load().then(open, open);
    else open();
  });
}

/* Pre-order tiles: keep "In your cart" and "Add another" in step with the
   cart. Adding is the shared [data-sbo3-add-cart] handler's job
   (assets/sbo-v3-cart-actions.js); this only listens. Liquid drew the
   state at load. */
function initHeroPreorder(root) {
  const grid = root.querySelector('[data-hero-preorder]');
  if (!grid || grid.dataset.cartReady) return;
  grid.dataset.cartReady = 'true';

  const firstLabel = grid.dataset.preorderLabel || 'Pre-order';
  const tiles = Array.from(grid.querySelectorAll('[data-pick-variant]'));
  const buttonOf = tile => tile.querySelector('[data-sbo3-add-cart]');

  function label(tile) {
    const button = buttonOf(tile);
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    const text = tile.hasAttribute('data-in-cart') ? 'Add another' : (button.dataset.idleLabel || firstLabel);
    button.querySelectorAll('[data-sbo3-action-label]').forEach(span => { span.textContent = text; });
    button.setAttribute('aria-label', `${text}: ${button.dataset.productTitle}`);
  }

  function apply(cart) {
    const ids = new Set(cart.items.map(item => String(item.variant_id)));
    tiles.forEach(tile => {
      const inCart = ids.has(tile.dataset.pickVariant);
      tile.toggleAttribute('data-in-cart', inCart);
      const button = buttonOf(tile);
      /* The button that was just pressed turns green while it says "Added". */
      if (button && inCart && button.getAttribute('aria-busy') === 'true') button.classList.add('is-added');
      label(tile);
    });
  }

  /* Only a whole cart (it has item_count) can say what is no longer in it;
     an /add.js reply lists just the lines it added, so then ask for the cart. */
  async function refresh(cart) {
    if (cart && Array.isArray(cart.items) && 'item_count' in cart) { apply(cart); return; }
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (response.ok) apply(await response.json());
    } catch (error) { /* The tiles keep their last state. */ }
  }

  document.addEventListener('cart:updated', event => refresh(event.detail && event.detail.cartData));
  /* Changes made in the cart drawer or on the cart page arrive through the
     theme's pub/sub (assets/pubsub.js, loaded deferred). */
  const listen = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, event => refresh(event && event.cartData));
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', listen);
  else listen();
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });

  /* When the shared handler is done it puts back the label the button had
     when it was pressed; the label that fits the cart goes on right after. */
  const observer = new MutationObserver(records => records.forEach(record => {
    const button = record.target;
    if (button.getAttribute('aria-busy') === 'true') return;
    button.classList.remove('is-added');
    label(button.closest('[data-pick-variant]'));
  }));
  tiles.forEach(tile => {
    const button = buttonOf(tile);
    if (button) observer.observe(button, { attributes: true, attributeFilter: ['aria-busy'] });
  });
}

/* Promo tiles: sale window, countdown ("Starts in" before the start, "Ends
   in" after it) and copy-to-clipboard for the code. Outside the window every
   promo tile is removed together and the main tile takes the whole grid
   (or sits beside the carousel), so the campaign never leaves a hole.
   Each countdown digit that changes replays a short slide-in; reduced
   motion turns that off in CSS. */
function initHeroPromo(root) {
  const promo = root.querySelector('[data-hero-promo]');
  if (!promo || promo.dataset.ready) return;
  promo.dataset.ready = 'true';

  const startsAt = Date.parse(promo.dataset.startsAt);
  const endsAt = Date.parse(promo.dataset.endsAt);
  const showBeforeStart = promo.dataset.showBeforeStart === 'true';
  const inEditor = Boolean(window.Shopify && window.Shopify.designMode);
  const label = promo.querySelector('[data-hero-promo-countdown-label]');
  const units = Object.fromEntries(Array.from(promo.querySelectorAll('[data-unit]')).map(el => [el.dataset.unit, el]));
  const pad = n => String(n).padStart(2, '0');
  let timer = null;

  function closePromo() {
    clearInterval(timer);
    const inner = root.querySelector('[data-hero-inner]');
    const starter = root.querySelector('[data-hero-starter]');
    const carousel = root.querySelector('[data-hero-carousel-column]');
    promo.remove();
    if (starter) starter.hidden = false;
    else if (carousel) carousel.hidden = false;
    else if (inner) inner.classList.add('sbov3hero__inner--no-media');
  }

  function show(unit, value) {
    const el = units[unit];
    if (!el || el.textContent === value) return;
    el.textContent = value;
    el.classList.remove('is-tick');
    void el.offsetWidth;
    el.classList.add('is-tick');
  }

  function tick() {
    /* "Preview after the campaign" in the theme editor shows the starter
       while the sale is still running. */
    if (inEditor && promo.dataset.previewStarter === 'true') { closePromo(); return; }
    const now = Date.now();
    const beforeStart = !Number.isNaN(startsAt) && now < startsAt;
    const outside = Number.isNaN(endsAt) || now >= endsAt || (beforeStart && !showBeforeStart);
    if (outside && !inEditor) { closePromo(); return; }
    if (Number.isNaN(endsAt)) return;
    const left = Math.max(0, Math.floor(((beforeStart ? startsAt : endsAt) - now) / 1000));
    show('days', String(Math.floor(left / 86400)));
    show('hours', pad(Math.floor((left % 86400) / 3600)));
    show('minutes', pad(Math.floor((left % 3600) / 60)));
    show('seconds', pad(left % 60));
    if (label) label.textContent = beforeStart ? 'Starts in' : 'Ends in';
  }

  tick();
  if (promo.isConnected) timer = setInterval(tick, 1000);

  const codeButton = promo.querySelector('[data-hero-promo-code]');
  if (codeButton) {
    const text = codeButton.querySelector('[data-hero-promo-code-text]');
    const status = promo.querySelector('[data-hero-promo-code-status]');
    const code = codeButton.dataset.heroPromoCode;
    let reset = null;
    codeButton.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(code);
      } catch (error) {
        const range = document.createRange();
        range.selectNodeContents(text);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        if (status) status.textContent = 'Code selected. Copy it with Ctrl+C or Cmd+C.';
        return;
      }
      clearTimeout(reset);
      codeButton.classList.add('is-copied');
      text.textContent = 'Copied';
      if (status) status.textContent = 'Code ' + code + ' copied';
      reset = setTimeout(() => {
        codeButton.classList.remove('is-copied');
        text.textContent = code;
      }, 1800);
    });
  }
}
